import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSocket, joinRoom, leaveRoom, signalPeer } from '../services/socket';
import { getIceServers } from '../services/webrtcConfig';
import { CameraOption, cameraOptions, friendlyCameraError, preferredCamera, selectedCameraConstraints } from '../utils/cameraSetup';
import { releaseMediaSession } from '../utils/mediaSession';

export type SenderStatus = 'idle' | 'previewing' | 'connecting' | 'connected' | 'reconnecting' | 'error' | 'ended';
type PermissionStatus = 'idle' | 'requesting' | 'granted' | 'denied' | 'unsupported';
type SignalPayload = RTCSessionDescriptionInit & { candidate?: RTCIceCandidateInit; control?: 'camera-switching' | 'camera-switched' };

export function usePhoneCameraSender(roomId: string | null) {
  const endedKey = roomId ? `examPhoneEnded:${roomId}` : '';
  const [stream, setStream] = useState<MediaStream>();
  const [devices, setDevices] = useState<CameraOption[]>([]);
  const [selectedDeviceId, setSelectedDeviceIdState] = useState('');
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>('idle');
  const [status, setStatus] = useState<SenderStatus>(() => endedKey && sessionStorage.getItem(endedKey) === 'true' ? 'ended' : 'idle');
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [startingPreview, setStartingPreview] = useState(false);
  const [done, setDone] = useState(false);
  const peerRef = useRef<RTCPeerConnection>();
  const senderRef = useRef<RTCRtpSender>();
  const streamRef = useRef<MediaStream>();
  const signalHandlerRef = useRef<({ payload }: { payload: SignalPayload }) => Promise<void>>();
  const sessionEndedHandlerRef = useRef<() => void>();
  const pageExitHandlerRef = useRef<() => void>();
  const socketRef = useRef<ReturnType<typeof getSocket>>();
  const intentionallyStoppedRef = useRef(status === 'ended');

  const setSelectedDeviceId = useCallback((deviceId: string) => {
    setSelectedDeviceIdState(deviceId);
  }, []);

  const stop = useCallback((reason: 'idle' | 'ended' = 'idle', updateState = true) => {
    if (reason === 'ended' && intentionallyStoppedRef.current && status === 'ended') return;
    intentionallyStoppedRef.current = true;
    const socket = socketRef.current;
    if (signalHandlerRef.current) socket?.off('webrtc:signal', signalHandlerRef.current);
    if (sessionEndedHandlerRef.current) socket?.off('webrtc:session-ended', sessionEndedHandlerRef.current);
    if (pageExitHandlerRef.current) {
      window.removeEventListener('pagehide', pageExitHandlerRef.current);
      window.removeEventListener('beforeunload', pageExitHandlerRef.current);
    }
    signalHandlerRef.current = undefined;
    sessionEndedHandlerRef.current = undefined;
    pageExitHandlerRef.current = undefined;
    releaseMediaSession({ stream: streamRef.current, peer: peerRef.current });
    peerRef.current = undefined;
    senderRef.current = undefined;
    streamRef.current = undefined;
    if (socket && roomId) leaveRoom(roomId);
    socketRef.current = undefined;
    if (reason === 'ended' && endedKey) sessionStorage.setItem(endedKey, 'true');
    if (updateState) {
      setStream(undefined);
      setStatus(reason);
      if (reason === 'ended') setMessage('Camera access has been stopped.');
    }
  }, [endedKey, roomId, status]);

  useEffect(() => {
    if (!roomId || status === 'ended') return;
    const socket = getSocket();
    socketRef.current = socket;
    const handleSessionEnded = () => stop('ended');
    const handlePageExit = () => stop('idle', false);
    sessionEndedHandlerRef.current = handleSessionEnded;
    pageExitHandlerRef.current = handlePageExit;
    socket.on('webrtc:session-ended', handleSessionEnded);
    joinRoom(roomId);
    window.addEventListener('pagehide', handlePageExit);
    window.addEventListener('beforeunload', handlePageExit);
    return () => {
      socket.off('webrtc:session-ended', handleSessionEnded);
      window.removeEventListener('pagehide', handlePageExit);
      window.removeEventListener('beforeunload', handlePageExit);
      leaveRoom(roomId);
    };
  }, [roomId, status, stop]);

  const requestPermission = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || !navigator.mediaDevices.enumerateDevices) {
      setPermissionStatus('unsupported');
      setError('Camera selection is unavailable. Open this page over HTTPS in a current mobile browser.');
      return false;
    }
    setPermissionStatus('requesting');
    setError(undefined);
    let temporary: MediaStream | undefined;
    try {
      temporary = await navigator.mediaDevices.getUserMedia({ video: selectedCameraConstraints(), audio: false });
      const track = temporary.getVideoTracks()[0];
      const fallbackDeviceId = track?.getSettings().deviceId ?? '';
      const settings = track?.getSettings();
      const enumerated = await navigator.mediaDevices.enumerateDevices();
      const sourceDevices = enumerated.some((device) => device.kind === 'videoinput') ? enumerated : fallbackDeviceId
        ? [{ kind: 'videoinput', deviceId: fallbackDeviceId, label: track.label }] as MediaDeviceInfo[]
        : [];
      const listed = cameraOptions(sourceDevices, fallbackDeviceId, settings?.facingMode);
      if (!listed.length) throw new DOMException('No video input devices', 'NotFoundError');
      setDevices(listed);
      setSelectedDeviceId(preferredCamera(listed, selectedDeviceId));
      setPermissionStatus('granted');
      return true;
    } catch (permissionError) {
      setPermissionStatus(permissionError instanceof DOMException && permissionError.name === 'NotAllowedError' ? 'denied' : 'idle');
      setError(friendlyCameraError(permissionError));
      return false;
    } finally {
      temporary?.getTracks().forEach((track) => track.stop());
    }
  }, [selectedDeviceId, setSelectedDeviceId]);

  const startPreview = useCallback(async (deviceId = selectedDeviceId) => {
    if (!navigator.mediaDevices?.getUserMedia || status === 'ended') return false;
    setStartingPreview(true);
    setError(undefined);
    if (senderRef.current && roomId) signalPeer(roomId, { control: 'camera-switching' });
    try {
      const nextStream = await navigator.mediaDevices.getUserMedia({ video: selectedCameraConstraints(deviceId), audio: false });
      if (intentionallyStoppedRef.current) {
        nextStream.getTracks().forEach((track) => track.stop());
        return false;
      }
      const nextTrack = nextStream.getVideoTracks()[0];
      if (!nextTrack) throw new DOMException('No video track', 'NotFoundError');
      if (senderRef.current) await senderRef.current.replaceTrack(nextTrack);
      const previous = streamRef.current;
      streamRef.current = nextStream;
      setStream(nextStream);
      setStatus(peerRef.current ? 'connected' : 'previewing');
      previous?.getTracks().forEach((track) => track.stop());
      return true;
    } catch (previewError) {
      setError(friendlyCameraError(previewError));
      setStatus('error');
      return false;
    } finally {
      if (senderRef.current && roomId) signalPeer(roomId, { control: 'camera-switched' });
      setStartingPreview(false);
    }
  }, [roomId, selectedDeviceId, status]);

  const connect = useCallback(async () => {
    if (!roomId || status === 'ended' || peerRef.current || !streamRef.current) return false;
    if (typeof RTCPeerConnection === 'undefined') {
      setError('WebRTC is not supported by this browser.');
      setStatus('error');
      return false;
    }
    intentionallyStoppedRef.current = false;
    setError(undefined);
    setStatus('connecting');
    try {
      const socket = socketRef.current ?? getSocket();
      socketRef.current = socket;
      joinRoom(roomId);
      const peer = new RTCPeerConnection({ iceServers: await getIceServers() });
      peerRef.current = peer;
      const track = streamRef.current.getVideoTracks()[0];
      senderRef.current = peer.addTrack(track, streamRef.current);
      peer.onicecandidate = (event) => {
        if (event.candidate) signalPeer(roomId, { candidate: event.candidate });
      };
      peer.onconnectionstatechange = () => {
        if (peer.connectionState === 'connected') {
          setMessage(undefined);
          setStatus('connected');
        } else if (peer.connectionState === 'disconnected' || peer.connectionState === 'connecting') {
          setMessage('Secondary camera connection was interrupted. Reconnecting...');
          setStatus('reconnecting');
        } else if (peer.connectionState === 'failed') {
          setError('The desktop connection was interrupted. Keep this page open while the connection recovers.');
          setStatus('error');
        }
      };
      const handleSignal = async ({ payload }: { payload: SignalPayload }) => {
        try {
          if (payload.control) return;
          if (payload.candidate) await peer.addIceCandidate(payload.candidate);
          else if (payload.type === 'answer') {
            await peer.setRemoteDescription(payload);
            setStatus('connected');
          }
        } catch {
          setError('The desktop connection failed. Keep both devices online and retry.');
          setStatus('error');
        }
      };
      signalHandlerRef.current = handleSignal;
      socket.on('webrtc:signal', handleSignal);
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      signalPeer(roomId, offer);
      return true;
    } catch {
      peerRef.current?.close();
      peerRef.current = undefined;
      senderRef.current = undefined;
      setError('The camera could not connect to the desktop. Check the connection and retry.');
      setStatus('error');
      return false;
    }
  }, [roomId, status]);

  const finish = useCallback(() => {
    setDone(true);
    try { window.close(); } catch { /* Browsers usually allow close only for script-opened tabs. */ }
  }, []);

  useEffect(() => () => {
    releaseMediaSession({ stream: streamRef.current, peer: peerRef.current });
  }, []);

  return useMemo(() => ({
    stream, devices, selectedDeviceId, setSelectedDeviceId, permissionStatus, status, error, message,
    startingPreview, done, requestPermission, startPreview, connect, stop, finish
  }), [connect, devices, done, error, finish, message, permissionStatus, requestPermission, selectedDeviceId, setSelectedDeviceId, startPreview, startingPreview, status, stop, stream]);
}
